import React, { useState, useEffect, useRef } from 'react';
import { 
  Users, 
  Play, 
  MessageSquare,
  Brain,
  Send,
  ThumbsUp,
  ThumbsDown,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Clock,
  Zap,
  CheckCircle2,
  Circle,
  Loader2,
  FileText,
  BarChart3,
  Share2,
  AlertCircle,
  TrendingUp,
  Database,
  Target,
  ArrowRight,
  Eye,
  History,
  Sparkles
} from 'lucide-react';
import { apiFetch } from '../services/api_service';
import { API } from '../App';

// Unified Agent configurations
const AGENTS = {
  'data_scientist': { name: 'Dr. Data', icon: '🔬', color: '#3B82F6', role: 'Data Scientist', phase: 'ingest' },
  'kpi_deduction': { name: 'KPI Oracle', icon: '📈', color: '#8B5CF6', role: 'KPI Specialist', phase: 'ingest' },
  'applied_scientist': { name: 'Dr. Benchmark', icon: '📊', color: '#10B981', role: 'Applied Scientist', phase: 'analyze' },
  'compliance_oracle': { name: 'Compliance Guardian', icon: '⚖️', color: '#F59E0B', role: 'Compliance Expert', phase: 'analyze' },
  'strategist': { name: 'Strategy Advisor', icon: '🎯', color: '#EF4444', role: 'ESG Strategist', phase: 'decide' },
  'report_architect': { name: 'Report Master', icon: '📝', color: '#06B6D4', role: 'Report Architect', phase: 'generate' },
  'team_lead': { name: 'Team Orchestrator', icon: '🤖', color: '#6366F1', role: 'Team Lead', phase: 'orchestrate' }
};

const WORKFLOW_PHASES = [
  { id: 'ingest', label: 'Ingest & Validate', icon: '📥', agents: ['data_scientist', 'kpi_deduction'] },
  { id: 'analyze', label: 'Analyze & Benchmark', icon: '📊', agents: ['applied_scientist', 'compliance_oracle'] },
  { id: 'brainstorm', label: 'Brainstorm', icon: '💡', agents: ['all'] },
  { id: 'prioritize', label: 'Prioritize', icon: '📋', agents: ['strategist'] },
  { id: 'decide', label: 'Decide', icon: '✅', agents: ['strategist', 'team_lead'] },
  { id: 'generate', label: 'Generate', icon: '📄', agents: ['report_architect'] }
];

// Tab components
const TabButton = ({ active, onClick, icon: Icon, label, badge }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
      active 
        ? 'bg-emerald-600 text-white shadow-md' 
        : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
    }`}
  >
    <Icon size={18} />
    {label}
    {badge && (
      <span className={`text-xs px-1.5 py-0.5 rounded-full ${
        active ? 'bg-white/20 text-white' : 'bg-emerald-100 text-emerald-700'
      }`}>
        {badge}
      </span>
    )}
  </button>
);

// Agent Avatar Component
const AgentAvatar = ({ agentType, size = 'md' }) => {
  const agent = AGENTS[agentType] || { icon: '🤖', color: '#6B7280' };
  const sizeClasses = {
    sm: 'w-8 h-8 text-lg',
    md: 'w-10 h-10 text-xl',
    lg: 'w-12 h-12 text-2xl'
  };
  
  return (
    <div 
      className={`${sizeClasses[size]} rounded-lg flex items-center justify-center`}
      style={{ backgroundColor: `${agent.color}20` }}
    >
      {agent.icon}
    </div>
  );
};

// Accountability Trail Component
const AccountabilityTrail = ({ actions }) => (
  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
    <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
      <History size={16} />
      Accountability Trail
    </h4>
    <div className="space-y-2 max-h-48 overflow-y-auto">
      {actions.map((action, idx) => (
        <div key={idx} className="flex items-start gap-2 text-sm">
          <AgentAvatar agentType={action.agent} size="sm" />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium text-slate-800">{AGENTS[action.agent]?.name || action.agent}</span>
              <span className="text-xs text-slate-400">{action.timestamp}</span>
            </div>
            <p className="text-slate-600">{action.action}</p>
          </div>
        </div>
      ))}
      {actions.length === 0 && (
        <p className="text-slate-400 text-sm">No actions recorded yet</p>
      )}
    </div>
  </div>
);

// Data Flow Visualization
const DataFlowVisualization = ({ currentPhase }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4">
    <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
      <Share2 size={16} className="text-blue-600" />
      Data Flow & Decision Path
    </h4>
    <div className="flex items-center justify-between overflow-x-auto pb-2">
      {WORKFLOW_PHASES.map((phase, idx) => {
        const isActive = phase.id === currentPhase;
        const isPast = WORKFLOW_PHASES.findIndex(p => p.id === currentPhase) > idx;
        
        return (
          <React.Fragment key={phase.id}>
            <div className={`flex flex-col items-center min-w-[80px] ${
              isActive ? 'scale-110' : ''
            }`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg mb-1 ${
                isActive ? 'bg-emerald-500 text-white ring-4 ring-emerald-100' :
                isPast ? 'bg-emerald-100 text-emerald-600' :
                'bg-slate-100 text-slate-400'
              }`}>
                {isPast ? <CheckCircle2 size={20} /> : phase.icon}
              </div>
              <span className={`text-xs text-center ${
                isActive ? 'font-semibold text-emerald-700' : 'text-slate-500'
              }`}>
                {phase.label}
              </span>
            </div>
            {idx < WORKFLOW_PHASES.length - 1 && (
              <ArrowRight size={16} className={`mx-1 flex-shrink-0 ${
                isPast ? 'text-emerald-400' : 'text-slate-300'
              }`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  </div>
);

// Chat Message Component
const ChatMessage = ({ message, onFeedback }) => {
  const agent = AGENTS[message.agent_type] || { name: 'Agent', icon: '🤖', color: '#6B7280' };
  const isUser = message.role === 'user';
  
  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      {!isUser && <AgentAvatar agentType={message.agent_type} />}
      <div className={`flex-1 max-w-[80%] ${isUser ? 'text-right' : ''}`}>
        {!isUser && (
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-slate-800">{agent.name}</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
              {agent.role}
            </span>
          </div>
        )}
        <div className={`rounded-xl px-4 py-3 ${
          isUser 
            ? 'bg-emerald-600 text-white ml-auto' 
            : 'bg-white border border-slate-200'
        }`}>
          <p className={`text-sm whitespace-pre-wrap ${isUser ? '' : 'text-slate-700'}`}>
            {message.content}
          </p>
        </div>
        {!isUser && onFeedback && (
          <div className="flex items-center gap-2 mt-1">
            <button 
              onClick={() => onFeedback(message.id, 'thumbs_up')}
              className="p-1 text-slate-400 hover:text-emerald-600 transition-colors"
            >
              <ThumbsUp size={14} />
            </button>
            <button 
              onClick={() => onFeedback(message.id, 'thumbs_down')}
              className="p-1 text-slate-400 hover:text-red-500 transition-colors"
            >
              <ThumbsDown size={14} />
            </button>
            <span className="text-xs text-slate-400">{message.timestamp}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Workflow Status Card
const WorkflowStatusCard = ({ workflow, onSelect }) => {
  const statusColors = {
    'completed': 'bg-emerald-100 text-emerald-700',
    'running': 'bg-blue-100 text-blue-700',
    'brainstorming': 'bg-amber-100 text-amber-700',
    'awaiting_approval': 'bg-purple-100 text-purple-700',
    'created': 'bg-slate-100 text-slate-600',
    'failed': 'bg-red-100 text-red-700'
  };
  
  return (
    <div 
      onClick={() => onSelect(workflow)}
      className="p-4 bg-white rounded-lg border border-slate-200 hover:border-emerald-300 hover:shadow-sm transition-all cursor-pointer"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="font-medium text-slate-800">{workflow.workflow_id}</span>
        <span className={`text-xs px-2 py-1 rounded-full ${statusColors[workflow.status] || 'bg-slate-100'}`}>
          {workflow.status}
        </span>
      </div>
      <div className="flex items-center gap-4 text-sm text-slate-500">
        <span className="flex items-center gap-1">
          <Clock size={14} />
          {new Date(workflow.created_at).toLocaleDateString()}
        </span>
        <span className="flex items-center gap-1">
          <Target size={14} />
          {workflow.current_phase}
        </span>
      </div>
    </div>
  );
};

// Automated Workflows Section - Shows workflows triggered by data uploads
const AutomatedWorkflowsSection = () => {
  const [activeRuns, setActiveRuns] = useState([]);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    fetchWorkflows();
    const interval = setInterval(fetchActiveWorkflows, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchActiveWorkflows = async () => {
    const data = await apiFetch(`${API}/automated-workflow/active`);
    if (data?._error) {
      console.log('No active automated workflows');
      return;
    }
    setActiveRuns(data.active_runs || []);
  };

  const fetchWorkflows = async () => {
    await fetchActiveWorkflows();
    const data = await apiFetch(`${API}/automated-workflow/history?limit=5`);
    if (data?._error) {
      console.log('No workflow history');
    } else {
      setHistory(data.runs || []);
    }
    setLoadingHistory(false);
  };

  const getStatusColor = (status) => {
    const colors = {
      'completed': 'bg-emerald-100 text-emerald-700 border-emerald-200',
      'failed': 'bg-red-100 text-red-700 border-red-200',
      'pending': 'bg-slate-100 text-slate-600 border-slate-200',
      'initializing': 'bg-blue-100 text-blue-700 border-blue-200',
      'phase_ingest': 'bg-blue-100 text-blue-700 border-blue-200',
      'phase_analyze': 'bg-purple-100 text-purple-700 border-purple-200',
      'phase_brainstorm': 'bg-amber-100 text-amber-700 border-amber-200',
      'phase_prioritize': 'bg-orange-100 text-orange-700 border-orange-200',
      'phase_decide': 'bg-cyan-100 text-cyan-700 border-cyan-200',
      'phase_generate': 'bg-indigo-100 text-indigo-700 border-indigo-200',
      'completing': 'bg-emerald-100 text-emerald-700 border-emerald-200'
    };
    return colors[status] || 'bg-slate-100 text-slate-600 border-slate-200';
  };

  if (activeRuns.length === 0 && history.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-slate-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Database size={20} className="text-emerald-600" />
            <h3 className="font-semibold text-slate-800">Data Upload Workflows</h3>
            {activeRuns.length > 0 && (
              <span className="px-2 py-0.5 bg-emerald-500 text-white text-xs rounded-full animate-pulse">
                {activeRuns.length} active
              </span>
            )}
          </div>
          {history.length > 0 && (
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1"
            >
              <History size={14} />
              {showHistory ? 'Hide' : 'Show'} History
            </button>
          )}
        </div>
        <p className="text-sm text-slate-500 mt-1">
          Workflows automatically triggered when you upload data
        </p>
      </div>

      <div className="p-4 space-y-3">
        {/* Active Runs */}
        {activeRuns.map(run => (
          <div 
            key={run.run_id}
            className={`p-4 rounded-lg border ${getStatusColor(run.status)}`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-xl">{run.display?.icon || '⏳'}</span>
                <div>
                  <span className="font-medium text-slate-800">{run.upload_filename}</span>
                  <p className="text-xs text-slate-500">{run.data_source_type}</p>
                </div>
              </div>
              <span className="text-xs font-medium">{run.progress}%</span>
            </div>
            <div className="w-full h-2 bg-white/50 rounded-full overflow-hidden">
              <div 
                className="h-full bg-current opacity-50 transition-all duration-500 rounded-full"
                style={{ width: `${run.progress}%` }}
              />
            </div>
            <p className="text-xs mt-2">{run.status_message || run.display?.description}</p>
          </div>
        ))}

        {/* History */}
        {showHistory && history.filter(h => !activeRuns.find(a => a.run_id === h.run_id)).map(run => (
          <div 
            key={run.run_id}
            className={`p-3 rounded-lg border ${getStatusColor(run.status)}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span>{run.display?.icon || '✅'}</span>
                <div>
                  <span className="font-medium text-sm text-slate-800">{run.upload_filename}</span>
                  <p className="text-xs text-slate-500">{run.data_source_type}</p>
                </div>
              </div>
              <div className="text-right">
                <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(run.status)}`}>
                  {run.display?.label || run.status}
                </span>
                {run.email_notification_sent && (
                  <p className="text-xs text-slate-400 mt-1">📧 Email sent</p>
                )}
              </div>
            </div>
          </div>
        ))}

        {loadingHistory && (
          <div className="text-center py-4 text-slate-400">
            <Loader2 size={20} className="animate-spin mx-auto" />
          </div>
        )}
      </div>
    </div>
  );
};

// Main Component
const AICommandCenter = () => {
  const [activeTab, setActiveTab] = useState('team');
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workflows, setWorkflows] = useState([]);
  const [activeWorkflow, setActiveWorkflow] = useState(null);
  const [runningWorkflow, setRunningWorkflow] = useState(false);
  
  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState('team_lead');
  const chatEndRef = useRef(null);
  
  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [knowledgeStats, setKnowledgeStats] = useState(null);
  
  // Accountability trail
  const [accountabilityTrail, setAccountabilityTrail] = useState([]);

  const fetchData = async () => {
    const [agentsData, workflowsData] = await Promise.all([
      apiFetch(`${API}/collaborative/agents`),
      apiFetch(`${API}/collaborative/workflows?limit=5`)
    ]);

    if (agentsData?._error || workflowsData?._error) {
      console.error('Error fetching data:', agentsData?._error ? agentsData.message : workflowsData.message);
      setLoading(false);
      return;
    }

    setAgents(agentsData.agents || []);
    setWorkflows(workflowsData.workflows || []);

    // Fetch analytics if available
    const [analyticsData, knowledgeData] = await Promise.all([
      apiFetch(`${API}/agents/analytics/overview?days=30`),
      apiFetch(`${API}/agents/knowledge-sharing/stats`)
    ]);
    if (analyticsData?._error || knowledgeData?._error) {
      console.log('Analytics not available');
    } else {
      setAnalytics(analyticsData);
      setKnowledgeStats(knowledgeData);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const startWorkflow = async () => {
    setRunningWorkflow(true);
    setAccountabilityTrail([]);
    
    // Add to accountability trail
    const addToTrail = (agent, action) => {
      setAccountabilityTrail(prev => [...prev, {
        agent,
        action,
        timestamp: new Date().toLocaleTimeString()
      }]);
    };

    addToTrail('team_lead', 'Workflow initiated - coordinating agent team');

    const data = await apiFetch(`${API}/collaborative/workflow/run-full`, {
      method: 'POST',
      body: {
        workflow_type: 'full_analysis',
        data_context: {
          company: 'Client Company',
          industry: 'Technology',
          reporting_year: 2025,
          frameworks: ['CSRD', 'GRI']
        },
        focus_areas: ['emissions', 'governance', 'social'],
        frameworks: ['CSRD', 'GRI']
      }
    });

    if (data?._error) {
      console.error('Error starting workflow:', data.message);
      setRunningWorkflow(false);
      return;
    }

    setActiveWorkflow(data);

    // Update trail based on phases
    if (data.phases) {
      Object.keys(data.phases).forEach(phase => {
        const phaseConfig = WORKFLOW_PHASES.find(p => p.id === phase);
        if (phaseConfig) {
          phaseConfig.agents.forEach(agentId => {
            if (agentId !== 'all') {
              addToTrail(agentId, `Completed ${phase} phase`);
            }
          });
        }
      });
    }

    fetchData();
    setRunningWorkflow(false);
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim() || chatLoading) return;
    
    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: chatInput,
      timestamp: new Date().toLocaleTimeString()
    };
    
    setChatMessages(prev => [...prev, userMessage]);
    setChatInput('');
    setChatLoading(true);
    
    // Use brainstorm endpoint for team discussion
    const data = await apiFetch(`${API}/collaborative/brainstorm`, {
      method: 'POST',
      body: {
        topic: chatInput,
        context: { mode: 'chat' },
        max_rounds: 1
      }
    });

    if (data?._error) {
      console.error('Error sending message:', data.message);
      setChatMessages(prev => [...prev, {
        id: Date.now(),
        role: 'assistant',
        agent_type: 'team_lead',
        content: 'I encountered an error processing your request. Please try again.',
        timestamp: new Date().toLocaleTimeString()
      }]);
      setChatLoading(false);
      return;
    }

    // Add agent responses from discussion
    if (data.discussion) {
      data.discussion.forEach((msg, idx) => {
        setTimeout(() => {
          setChatMessages(prev => [...prev, {
            id: Date.now() + idx,
            role: 'assistant',
            agent_type: msg.agent_type,
            content: msg.content,
            timestamp: new Date().toLocaleTimeString()
          }]);
        }, idx * 500);
      });
    }

    // Add consensus if available
    if (data.consensus?.summary) {
      setTimeout(() => {
        setChatMessages(prev => [...prev, {
          id: Date.now() + 100,
          role: 'assistant',
          agent_type: 'team_lead',
          content: `**Team Consensus:**\n\n${data.consensus.summary}`,
          timestamp: new Date().toLocaleTimeString()
        }]);
      }, (data.discussion?.length || 0) * 500 + 500);
    }

    setChatLoading(false);
  };

  const handleFeedback = async (messageId, feedbackType) => {
    const data = await apiFetch(`${API}/agents/feedback`, {
      method: 'POST',
      body: {
        session_id: 'command-center',
        message_id: String(messageId),
        agent_type: 'team_lead',
        signal_type: feedbackType,
        query: 'command center interaction',
        response: ''
      }
    });
    if (data?._error) {
      console.log('Feedback recorded');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-4">
          <Loader2 size={40} className="animate-spin text-emerald-600" />
          <p className="text-slate-500">Loading AI Command Center...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ai-command-center">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Brain className="text-emerald-600" />
            AI Orchestrator
          </h1>
          <p className="text-slate-500 mt-1">
            Unified interface for your AI agent team - transparent, accountable, and cohesive
          </p>
        </div>
        
        <button
          onClick={startWorkflow}
          disabled={runningWorkflow}
          data-testid="start-analysis-btn"
          className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
        >
          {runningWorkflow ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Running Analysis...
            </>
          ) : (
            <>
              <Play size={18} />
              Start Full Analysis
            </>
          )}
        </button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-50 rounded-lg">
              <Users size={20} className="text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{agents.length}</p>
              <p className="text-sm text-slate-500">AI Agents</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <FileText size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{workflows.length}</p>
              <p className="text-sm text-slate-500">Workflows</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-50 rounded-lg">
              <TrendingUp size={20} className="text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">
                {analytics?.overall_metrics?.satisfaction_rate || 0}%
              </p>
              <p className="text-sm text-slate-500">Satisfaction</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-50 rounded-lg">
              <Database size={20} className="text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">
                {knowledgeStats?.total_shared_insights || 0}
              </p>
              <p className="text-sm text-slate-500">Shared Insights</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-2">
        <TabButton 
          active={activeTab === 'team'} 
          onClick={() => setActiveTab('team')}
          icon={Users}
          label="Agent Team"
        />
        <TabButton 
          active={activeTab === 'chat'} 
          onClick={() => setActiveTab('chat')}
          icon={MessageSquare}
          label="Team Chat"
        />
        <TabButton 
          active={activeTab === 'workflows'} 
          onClick={() => setActiveTab('workflows')}
          icon={Zap}
          label="Workflows"
          badge={workflows.length}
        />
        <TabButton 
          active={activeTab === 'insights'} 
          onClick={() => setActiveTab('insights')}
          icon={Eye}
          label="Transparency"
        />
      </div>

      {/* Tab Content */}
      <div className="min-h-[500px]">
        {/* Agent Team Tab */}
        {activeTab === 'team' && (
          <div className="space-y-6">
            {/* Value Prop */}
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-5 text-white">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Sparkles size={24} />
                </div>
                <div>
                  <h2 className="text-lg font-bold mb-1">1-2 Staff + AI Team = Complete ESG Function</h2>
                  <p className="text-emerald-100 text-sm">
                    Your virtual ESG department handles data processing, compliance, benchmarking, and reporting.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Data Flow */}
            <DataFlowVisualization currentPhase={activeWorkflow?.current_phase || 'ingest'} />
            
            {/* Agent Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {agents.map((agent) => (
                <div 
                  key={agent.type}
                  className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <AgentAvatar agentType={agent.type} size="lg" />
                    <div>
                      <h4 className="font-semibold text-slate-800">{agent.name}</h4>
                      <p className="text-xs text-slate-500">{agent.role}</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 mb-3 line-clamp-2">{agent.expertise}</p>
                  <div className="flex flex-wrap gap-1">
                    {agent.capabilities?.slice(0, 2).map((cap, idx) => (
                      <span 
                        key={idx}
                        className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600"
                      >
                        {cap.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Accountability */}
            {accountabilityTrail.length > 0 && (
              <AccountabilityTrail actions={accountabilityTrail} />
            )}
          </div>
        )}

        {/* Chat Tab */}
        {activeTab === 'chat' && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200 bg-slate-50">
              <h3 className="font-semibold text-slate-800">Team Discussion</h3>
              <p className="text-sm text-slate-500">Ask your AI team anything - they'll discuss and reach consensus</p>
            </div>
            
            <div className="h-[400px] overflow-y-auto p-4 space-y-4 bg-slate-50">
              {chatMessages.length === 0 && (
                <div className="text-center py-12 text-slate-400">
                  <MessageSquare size={40} className="mx-auto mb-3 opacity-50" />
                  <p>Start a conversation with your AI team</p>
                  <p className="text-sm">They'll collaborate and provide unified recommendations</p>
                </div>
              )}
              {chatMessages.map((msg) => (
                <ChatMessage 
                  key={msg.id} 
                  message={msg} 
                  onFeedback={msg.role !== 'user' ? handleFeedback : null}
                />
              ))}
              {chatLoading && (
                <div className="flex items-center gap-2 text-slate-500">
                  <Loader2 size={16} className="animate-spin" />
                  <span className="text-sm">Team is discussing...</span>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
            
            <div className="p-4 border-t border-slate-200 bg-white">
              <div className="flex gap-3">
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && sendChatMessage()}
                  placeholder="Ask your AI team anything..."
                  className="flex-1 px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  data-testid="chat-input"
                />
                <button
                  onClick={sendChatMessage}
                  disabled={chatLoading || !chatInput.trim()}
                  className="px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                  data-testid="chat-send-btn"
                >
                  <Send size={18} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Workflows Tab */}
        {activeTab === 'workflows' && (
          <div className="space-y-6">
            {/* Automated Workflows Section */}
            <AutomatedWorkflowsSection />
            
            {/* Collaborative Workflows */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-800">Manual Workflows</h3>
                {workflows.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
                    <FileText size={40} className="mx-auto mb-3 text-slate-300" />
                    <p className="text-slate-500">No manual workflows yet</p>
                    <p className="text-sm text-slate-400">Start a full analysis to create one</p>
                  </div>
                ) : (
                  workflows.map((wf) => (
                    <WorkflowStatusCard 
                      key={wf.workflow_id} 
                      workflow={wf}
                      onSelect={setActiveWorkflow}
                    />
                  ))
                )}
              </div>
              
              {activeWorkflow && (
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h3 className="font-semibold text-slate-800 mb-4">
                    Workflow: {activeWorkflow.workflow_id}
                  </h3>
                  <DataFlowVisualization currentPhase={activeWorkflow.current_phase || 'generate'} />
                  
                  {activeWorkflow.phases && (
                    <div className="mt-4 space-y-2">
                      {Object.entries(activeWorkflow.phases).map(([phase, data]) => (
                        <details key={phase} className="border border-slate-200 rounded-lg">
                          <summary className="p-3 cursor-pointer bg-slate-50 hover:bg-slate-100 font-medium text-slate-700">
                            {WORKFLOW_PHASES.find(p => p.id === phase)?.icon} {phase}
                            <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                              data.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                            }`}>
                              {data.status}
                            </span>
                          </summary>
                          <div className="p-3 text-sm">
                            <pre className="whitespace-pre-wrap text-slate-600 max-h-48 overflow-auto">
                              {JSON.stringify(data.outputs, null, 2)}
                            </pre>
                          </div>
                        </details>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Transparency Tab */}
        {activeTab === 'insights' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Knowledge Flow */}
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <Share2 size={18} className="text-blue-600" />
                  Knowledge Sharing
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-600">Shared Insights</span>
                    <span className="font-bold text-slate-800">{knowledgeStats?.total_shared_insights || 0}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-600">Knowledge Propagations</span>
                    <span className="font-bold text-slate-800">{knowledgeStats?.total_propagations || 0}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-600">Graph Connections</span>
                    <span className="font-bold text-slate-800">{knowledgeStats?.knowledge_graph_connections || 0}</span>
                  </div>
                </div>
              </div>
              
              {/* Agent Performance */}
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <BarChart3 size={18} className="text-purple-600" />
                  Agent Performance
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-600">Overall Satisfaction</span>
                    <span className="font-bold text-emerald-600">
                      {analytics?.overall_metrics?.satisfaction_rate || 0}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-600">Total Feedback</span>
                    <span className="font-bold text-slate-800">
                      {analytics?.overall_metrics?.total_feedback || 0}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-600">Learning Examples</span>
                    <span className="font-bold text-slate-800">
                      {analytics?.overall_metrics?.total_learning_examples || 0}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Data Flow Explanation */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
              <h3 className="font-semibold text-emerald-800 mb-3 flex items-center gap-2">
                <Eye size={18} />
                How Your Data Flows
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-emerald-700">
                <div className="flex items-start gap-2">
                  <span className="w-6 h-6 rounded-full bg-emerald-200 flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                  <p><strong>No Silos:</strong> All agents share a unified knowledge base and can access each other's insights</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-6 h-6 rounded-full bg-emerald-200 flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                  <p><strong>Accountability:</strong> Every action is logged with the responsible agent and timestamp</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-6 h-6 rounded-full bg-emerald-200 flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                  <p><strong>Continuous Learning:</strong> Feedback improves all agents through shared knowledge propagation</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AICommandCenter;
